import { getIdToken } from '@pooflabs/web';
import { AlertTriangle, Check, Loader2, Plus, Rocket, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { createAuthenticatedApiClient } from '@/lib/api-client';
import { rentPricePerHourForTier, rentPricePerSecForTier } from '@/utils/rentalPricing';
import { formatDuration, formatSol } from '@/utils/format';
import { WORKLOAD_PRESETS } from '@/utils/deploymentPresets';
import { getTierMeta, TIERS, type GpuTier } from '@/utils/gpuNetwork';

interface DeployWorkloadFormProps {
  walletAddress: string;
  balanceLamports: number;
  hasActiveRental: boolean;
}

export interface DeployResult {
  rentalId: string;
  nodeId: string;
  matchedTier: string;
  ratePerSecLamports: number;
  expiresAt: number;
  node: {
    gpuModel: string;
    label: string;
    tier: string;
    reputationScore: number;
    completedJobs: number;
  };
}

interface EnvRow {
  id: string;
  key: string;
  value: string;
}

const TIER_DOT: Record<string, string> = {
  low: 'bg-slate-400',
  mid: 'bg-sky-500',
  high: 'bg-violet-500',
  datacenter: 'bg-[#00C805]',
};

/**
 * Nosana-style deployment form. Replaces manual node picking — the renter
 * just describes the workload (image + optional command/env vars) and a
 * required GPU tier, then the backend auto-matches the best-rated available
 * node and starts billing by the second. All writes go through the
 * authenticated backend (POST /api/deployments/create) — rentals/gpuNodes
 * are vault-only.
 */
export const DeployWorkloadForm: React.FC<
  DeployWorkloadFormProps & { onDeployed: (result: DeployResult) => void }
> = ({ walletAddress, balanceLamports, hasActiveRental, onDeployed }) => {
  const [presetKey, setPresetKey] = useState<string>('pytorch');
  const [containerImage, setContainerImage] = useState(WORKLOAD_PRESETS[0].containerImage);
  const [command, setCommand] = useState('');
  const [envRows, setEnvRows] = useState<EnvRow[]>([]);
  const [tier, setTier] = useState<GpuTier>('mid');
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const ratePerSecLamports = rentPricePerSecForTier(tier);
  const ratePerHourLamports = rentPricePerHourForTier(tier);
  const remainingSeconds = ratePerSecLamports > 0 ? Math.floor(balanceLamports / ratePerSecLamports) : 0;

  const applyPreset = (key: string) => {
    setPresetKey(key);
    const preset = WORKLOAD_PRESETS.find((p) => p.key === key);
    if (preset) setContainerImage(preset.containerImage);
    setInlineError(null);
  };

  const addEnvRow = () => setEnvRows((rows) => [...rows, { id: crypto.randomUUID(), key: '', value: '' }]);
  const updateEnvRow = (id: string, field: 'key' | 'value', value: string) =>
    setEnvRows((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  const removeEnvRow = (id: string) => setEnvRows((rows) => rows.filter((r) => r.id !== id));

  const canSubmit = containerImage.trim().length > 0 && !hasActiveRental && !submitting;

  const handleDeploy = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setInlineError(null);
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Please reconnect your wallet and try again.');
        return;
      }
      const authApi = createAuthenticatedApiClient(token, walletAddress);

      const envVars = envRows.reduce<Record<string, string>>((acc, row) => {
        if (row.key.trim()) acc[row.key.trim()] = row.value;
        return acc;
      }, {});

      const body: Record<string, unknown> = {
        containerImage: containerImage.trim(),
        requiredTier: tier,
      };
      if (command.trim()) body.command = command.trim();
      if (Object.keys(envVars).length > 0) body.envVars = envVars;

      const result = await authApi.post<DeployResult>('/api/deployments/create', body);
      toast.success(`Deployed to ${result.node.gpuModel} — billing by the second.`);
      onDeployed(result);
    } catch (err) {
      console.error(err);
      const message = err instanceof Error && err.message ? err.message : 'Could not deploy this workload. Please try again.';
      setInlineError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-3xl border border-border bg-card p-5 sm:p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <Rocket className="w-4 h-4 text-primary" />
        <h3 className="font-extrabold text-lg tracking-tight">Deploy a workload</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Describe your container — we'll auto-match it to the best-rated available GPU node.
      </p>

      {/* ── Presets ── */}
      <label className="block text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">
        Start from a template
      </label>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-5">
        {WORKLOAD_PRESETS.map((preset) => {
          const active = preset.key === presetKey;
          return (
            <button
              key={preset.key}
              type="button"
              onClick={() => applyPreset(preset.key)}
              className={`relative text-left rounded-2xl border p-3.5 transition-all duration-300 ${
                active
                  ? 'border-primary bg-accent shadow-md -translate-y-0.5'
                  : 'border-border bg-background hover:border-primary/40 hover:-translate-y-0.5'
              }`}
            >
              {active && (
                <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                  <Check className="w-2.5 h-2.5 text-primary-foreground" strokeWidth={3} />
                </span>
              )}
              <p className="text-sm font-extrabold">{preset.label}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{preset.blurb}</p>
            </button>
          );
        })}
      </div>

      {/* ── Container image ── */}
      <div className="mb-4">
        <label className="block text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">
          Container image
        </label>
        <input
          value={containerImage}
          onChange={(e) => {
            setContainerImage(e.target.value);
            setPresetKey('custom');
          }}
          placeholder="e.g. pytorch/pytorch:latest"
          className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {/* ── Startup command ── */}
      <div className="mb-4">
        <label className="block text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">
          Startup command <span className="text-muted-foreground/60 font-normal">(optional)</span>
        </label>
        <input
          value={command}
          onChange={(e) => setCommand(e.target.value)}
          placeholder="e.g. python train.py --epochs 10"
          className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {/* ── Env vars ── */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-xs font-bold uppercase tracking-wide text-muted-foreground">
            Environment variables <span className="text-muted-foreground/60 font-normal">(optional)</span>
          </label>
          <button
            type="button"
            onClick={addEnvRow}
            className="text-xs font-semibold text-primary inline-flex items-center gap-1 transition-transform duration-300 hover:translate-x-0.5"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        {envRows.length === 0 ? (
          <p className="text-[11px] text-muted-foreground/70">No environment variables set.</p>
        ) : (
          <div className="space-y-2">
            {envRows.map((row) => (
              <div key={row.id} className="flex items-center gap-2">
                <input
                  value={row.key}
                  onChange={(e) => updateEnvRow(row.id, 'key', e.target.value)}
                  placeholder="KEY"
                  className="w-2/5 rounded-xl border border-input bg-background px-3 py-2 text-xs font-mono outline-none focus:ring-2 focus:ring-ring"
                />
                <input
                  value={row.value}
                  onChange={(e) => updateEnvRow(row.id, 'value', e.target.value)}
                  placeholder="value"
                  className="flex-1 rounded-xl border border-input bg-background px-3 py-2 text-xs font-mono outline-none focus:ring-2 focus:ring-ring"
                />
                <button
                  type="button"
                  onClick={() => removeEnvRow(row.id)}
                  aria-label="Remove variable"
                  className="w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Tier picker ── */}
      <label className="block text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">
        Required GPU tier
      </label>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-3">
        {TIERS.map((t) => {
          const active = t.key === tier;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTier(t.key)}
              className={`relative text-left rounded-2xl border p-3.5 transition-all duration-300 ${
                active
                  ? 'border-primary bg-accent shadow-md -translate-y-0.5'
                  : 'border-border bg-background hover:border-primary/40 hover:-translate-y-0.5'
              }`}
            >
              {active && (
                <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                  <Check className="w-2.5 h-2.5 text-primary-foreground" strokeWidth={3} />
                </span>
              )}
              <span
                className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white mb-1.5 ${
                  TIER_DOT[t.key] ?? 'bg-slate-400'
                }`}
              >
                {t.label}
              </span>
              <p className="text-[11px] text-muted-foreground">{t.vramRange}</p>
              <p className="text-sm font-bold text-primary mt-1.5">{formatSol(rentPricePerHourForTier(t.key), 4)}/hr</p>
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground mb-5">{getTierMeta(tier).blurb}</p>

      {/* ── Cost calculator ── */}
      <div className="rounded-2xl border border-primary/30 bg-accent/60 p-4 mb-5 grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-accent-foreground/70">Est. cost</p>
          <p className="text-lg font-extrabold tabular-nums tracking-tight text-accent-foreground mt-0.5">
            {formatSol(ratePerHourLamports, 4)} <span className="text-xs font-bold">/hr</span>
          </p>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-accent-foreground/70">
            Your balance lasts
          </p>
          <p className="text-lg font-extrabold tabular-nums tracking-tight text-accent-foreground mt-0.5">
            {balanceLamports > 0 ? formatDuration(remainingSeconds) : '—'}
          </p>
        </div>
      </div>

      {inlineError && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 flex items-start gap-2.5 text-sm text-destructive mb-5">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{inlineError}</span>
        </div>
      )}

      {hasActiveRental && (
        <div className="rounded-2xl bg-secondary/60 px-4 py-3 text-[12px] text-muted-foreground mb-5">
          You already have an active deployment. Stop it before deploying another.
        </div>
      )}

      <button
        onClick={handleDeploy}
        disabled={!canSubmit}
        className="w-full rounded-full bg-[#00C805] text-white font-bold py-3.5 shadow-lg shadow-[#00C805]/25 hover:bg-[#00b004] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
        {submitting ? 'Matching a node…' : 'Deploy workload'}
      </button>
    </div>
  );
};

export default DeployWorkloadForm;
